<template>
  <component
    :is="component"
    class="transition min-w-[20px] h-(--height) inline-flex items-center relative justify-center font-semibold"
    :class="[
      definition.classes,
      {
        'cursor-pointer': !!$attrs.onClick,
        'opacity-50 !cursor-not-allowed': disabled,
      },
    ]"
    :disabled="disabled"
    :style="{
      '--height': `${definition.height}px`,
    }"
    v-bind="bind"
    :tabindex="$attrs.onClick ? 0 : undefined"
  >
    <slot name="prepend">
      <app-icon v-if="icon" :icon="icon" :size="definition.iconSize" />
    </slot>

    <slot />

    <app-icon
      v-if="appendIcon"
      :icon="appendIcon"
      :size="definition.iconSize"
    />

    <button
      v-if="deletable"
      class="relative cursor-pointer before:absolute before:-inset-1 before:content-['']"
      type="button"
      @click="$emit('delete')"
    >
      <app-icon icon="ph:x" :size="16" />
    </button>
  </component>
</template>

<script lang="ts" setup>
import type { ChipProps } from "../chip.model";

const NuxtLink = resolveComponent("nuxt-link");

const properties = withDefaults(defineProps<ChipProps>(), {
  color: undefined,
  to: undefined,
  size: undefined,
  href: undefined,
  tag: "span",
  icon: undefined,
  appendIcon: undefined,
});

defineEmits(["delete"]);

const definition = computed<{
  height: number;
  iconSize: number;
  classes: string;
}>(() => {
  if (properties.size === "x-small")
    return {
      height: 16,
      iconSize: 14,
      classes: "gap-1 px-1 text-xs !font-normal rounded-sm",
    };

  if (properties.size === "small")
    return {
      height: 22,
      iconSize: 16,
      classes: "gap-1 px-1 text-xs rounded-sm",
    };

  return {
    height: 35,
    iconSize: 24,
    classes: "px-3 gap-2 text-sm sm:text-base rounded",
  };
});

const bind = computed(() => {
  const binding = new Map([]);

  binding.set("style", { backgroundColor: properties.color });

  if (properties.to) {
    binding.set("to", properties.to);
  }

  if (properties.href) {
    binding.set("href", properties.href);
    binding.set("target", "_blank");
  }

  return Object.fromEntries(binding.entries());
});

const component = computed(() => {
  if (properties.to) return NuxtLink;
  if (properties.href) return "a";

  return properties.tag;
});
</script>
